/**
 * Панель детализации.
 *
 * Открывается из строки, по которой кликнули. Из прямоугольника строки
 * к месту панели летит подсвеченный слой (цвет выбранной строки, рамка
 * акцента) — пользователь видит, откуда взялась панель. Сама панель с
 * содержимым проявляется, когда слой почти долетел: сплющенного текста
 * в полёте нет, а содержимое не ждёт отдельной задержки. Закрытие —
 * обратно в строку, если она ещё на экране.
 *
 * На телефоне это лист снизу: ручка, закрытие свайпом вниз, высота
 * по содержимому до 88% экрана. При reduced motion — только короткий fade.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '../components/Icon'
import { durationToken, prefersReducedMotion } from './motion'
import { useDialog } from '../components/useDialog'

export interface DrawerOrigin {
  element: HTMLElement | null
  rect: DOMRect | null
}

export function originFrom(element: HTMLElement | null): DrawerOrigin {
  return { element, rect: element?.getBoundingClientRect() ?? null }
}

function flipTransform(from: DOMRect, to: DOMRect): string {
  const sx = Math.max(0.02, from.width / to.width)
  const sy = Math.max(0.02, from.height / to.height)
  return `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${sx}, ${sy})`
}

const isSheet = () => window.matchMedia('(max-width: 640px)').matches

export function Drawer({
  open,
  origin,
  onClose,
  title,
  subtitle,
  children,
}: {
  open: boolean
  origin: DrawerOrigin | null
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
}) {
  const [mounted, setMounted] = useState(open)
  const [phase, setPhase] = useState<'enter' | 'open' | 'leave'>('enter')
  const panel = useRef<HTMLDivElement>(null)
  const ghost = useRef<HTMLDivElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const [flip, setFlip] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      setPhase('enter')
    } else if (mounted) {
      setPhase('leave')
    }
  }, [open])

  /** Поставить слой в прямоугольник from относительно места панели. */
  const placeGhost = (from: DOMRect, to: DOMRect, animate: boolean) => {
    const node = ghost.current
    if (!node) return
    node.style.left = `${to.left}px`
    node.style.top = `${to.top}px`
    node.style.width = `${to.width}px`
    node.style.height = `${to.height}px`
    if (!animate) node.style.transition = 'none'
    node.style.transform = flipTransform(from, to)
    if (!animate) {
      node.getBoundingClientRect()
      node.style.transition = ''
    }
  }

  // Вход: слой стартует в строке и в следующем кадре летит к панели.
  useLayoutEffect(() => {
    const node = panel.current
    if (!mounted || phase !== 'enter' || !node) return

    const from = origin?.rect
    const useFlip = Boolean(from) && !prefersReducedMotion() && !isSheet()
    setFlip(useFlip)
    if (useFlip && from) placeGhost(from, node.getBoundingClientRect(), false)

    const id = requestAnimationFrame(() => {
      if (useFlip && ghost.current) ghost.current.style.transform = 'none'
      setPhase('open')
      closeButton.current?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(id)
  }, [mounted, phase, origin])

  // Уход: панель гаснет сразу, слой возвращается в строку, если она жива.
  useEffect(() => {
    const node = panel.current
    if (phase !== 'leave' || !node) return

    const target = origin?.element?.isConnected ? origin.element.getBoundingClientRect() : null
    if (flip && target) placeGhost(target, node.getBoundingClientRect(), true)
    const id = window.setTimeout(() => {
      setMounted(false)
      setFlip(false)
      origin?.element?.focus({ preventScroll: true })
    }, durationToken('--dur-leave', 200))
    return () => window.clearTimeout(id)
  }, [phase, origin])

  // Escape из любой точки, Tab по кругу внутри панели, фон не прокручивается.
  // Фокус в строку, из которой открыли, панель возвращает сама — после полёта.
  useDialog(panel, { active: open, onClose, initialFocus: closeButton, returnFocus: origin?.element ? false : undefined })

  // --- Свайп вниз на листе ----------------------------------------------------
  // Тянуть можно за ручку и шапку; лист едет за пальцем transform'ом,
  // отпущенный дальше четверти своей высоты — закрывается.
  const drag = useRef<{ start: number; delta: number } | null>(null)
  const onTouchStart = (event: React.TouchEvent) => {
    if (!isSheet()) return
    drag.current = { start: event.touches[0]!.clientY, delta: 0 }
    if (panel.current) panel.current.style.transition = 'none'
  }
  const onTouchMove = (event: React.TouchEvent) => {
    if (!drag.current || !panel.current) return
    drag.current.delta = Math.max(0, event.touches[0]!.clientY - drag.current.start)
    panel.current.style.transform = `translateY(${drag.current.delta}px)`
  }
  const onTouchEnd = () => {
    const node = panel.current
    if (!drag.current || !node) return
    const { delta } = drag.current
    drag.current = null
    if (delta > node.getBoundingClientRect().height / 4) {
      // Отпущенный лист уезжает вниз от того места, где его отпустили,
      // а не гаснет на месте: инлайн-сдвиг перебил бы класс ухода.
      node.style.transition = 'transform var(--dur-leave) var(--ease-exit)'
      node.style.opacity = '1'
      node.style.transform = 'translateY(100%)'
      onClose()
    } else {
      node.style.transition = ''
      node.style.transform = ''
    }
  }

  if (!mounted) return null

  return createPortal(
    <div className={`drawer-layer drawer-layer--${phase} drawer-layer--${flip ? 'flip' : 'slide'}`}>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <div ref={ghost} className="drawer-ghost" aria-hidden="true" />
      <div ref={panel} className="drawer" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Детализация'} tabIndex={-1}>
        <div className="drawer__grip" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} aria-hidden="true">
          <span className="drawer__handle" />
        </div>
        <header className="drawer__header" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 className="drawer__title">{title}</h2>
            {subtitle && <div className="drawer__subtitle">{subtitle}</div>}
          </div>
          <button ref={closeButton} type="button" className="drawer__close" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" />
          </button>
        </header>
        <div className="drawer__body">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
